/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMapMarkerAlt, faEdit, faSave, faTimes, faUser, faBriefcase,
  faBuilding, faLink, faCheckCircle, faArrowLeft, faEnvelope,
  faPhone, faGlobe, faUsers, faUserTie, faHandshake, faShareAlt,
  faPlus, faInfoCircle, faImage, faUpload, faExternalLinkAlt, faCamera, faSearch, faBars
} from '@fortawesome/free-solid-svg-icons';
import {
  faLinkedin, faFacebook, faTwitter, faInstagram
} from '@fortawesome/free-brands-svg-icons';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import { useAuth } from '../context/AuthContext';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { safeLeafletSetView } from '../utils/safeLeaflet';
import GoogleMapEmbed from '../components/GoogleMapEmbed';
import globalSkillsData from '../data/global_skills.json';
import { STANDARD_INDUSTRIES } from '../utils/industryUtils';
import { ASSOCIATIONS, COMMUNITIES, ALL_OPTIONS, COUNTRIES as COMMUNITY_COUNTRIES } from '../data/communitiesAndAssociations';
import CommunityMemberBadge from '../components/CommunityMemberBadge';
import '../styles/MyProfileMobile.css';
import '../styles/EmployerMyProfileMobile.css';

const MOBILE_HEADER_HEIGHT = 56;
const MOBILE_SIDEBAR_WIDTH = 260;

// Community/Association Selector Component (reusable)
const CommunityAssociationSelector = ({ value = [], onChange }) => {
  const [selectedItems, setSelectedItems] = useState(value);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [manualInput, setManualInput] = useState({ name: '', type: 'community', country: '' });
  const [showManualForm, setShowManualForm] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSelectedItems(value);
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddFromDropdown = (option) => {
    const newItem = {
      name: option.name,
      type: option.type,
      country: ''
    };
    if (!selectedItems.some(item => item.name === option.name && item.type === option.type)) {
      const updated = [...selectedItems, newItem];
      setSelectedItems(updated);
      onChange(updated);
    }
    setDropdownOpen(false);
    setSearchQuery('');
  };

  const handleAddManual = () => {
    if (manualInput.name.trim()) {
      const newItem = {
        name: manualInput.name.trim(),
        type: manualInput.type,
        country: manualInput.country
      };
      if (!selectedItems.some(item => item.name === newItem.name && item.type === newItem.type)) {
        const updated = [...selectedItems, newItem];
        setSelectedItems(updated);
        onChange(updated);
        setManualInput({ name: '', type: 'community', country: '' });
        setShowManualForm(false);
      }
    }
  };

  const handleRemove = (index) => {
    const updated = selectedItems.filter((_, i) => i !== index);
    setSelectedItems(updated);
    onChange(updated);
  };

  const handleUpdateCountry = (index, country) => {
    const updated = selectedItems.map((item, i) => 
      i === index ? { ...item, country } : item
    );
    setSelectedItems(updated);
    onChange(updated);
  };

  // Filter and group options
  const availableOptions = ALL_OPTIONS.filter(opt => 
    !selectedItems.some(item => item.name === opt.name && item.type === opt.type)
  );

  const filteredOptions = availableOptions.filter(opt =>
    opt.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const associations = filteredOptions.filter(opt => opt.type === 'association');
  const communities = filteredOptions.filter(opt => opt.type === 'community');

  return (
    <div style={{ width: '100%' }}>
      {/* Dropdown Selector with Search */}
      <div style={{ position: 'relative', marginBottom: '10px' }} ref={dropdownRef}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #ddd',
                borderRadius: '6px',
                background: 'white',
                cursor: 'pointer'
              }}
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <div style={{ padding: '8px 12px', color: '#666' }}>
                <FontAwesomeIcon icon={faSearch} />
              </div>
              <input
                type="text"
                placeholder="Search communities/associations..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setDropdownOpen(true);
                }}
                onFocus={() => setDropdownOpen(true)}
                style={{
                  flex: 1,
                  padding: '10px',
                  border: 'none',
                  outline: 'none',
                  fontSize: '14px',
                  borderRadius: '6px'
                }}
              />
            </div>

            {/* Custom Dropdown */}
            {dropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: 'white',
                border: '1px solid #ddd',
                borderRadius: '6px',
                marginTop: '4px',
                maxHeight: '400px',
                overflowY: 'auto',
                zIndex: 1000,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}>
                {filteredOptions.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                    No results found
                  </div>
                ) : (
                  <>
                    {/* Associations Section */}
                    {associations.length > 0 && (
                      <div>
                        <div style={{
                          padding: '10px 15px',
                          background: '#f8f9fa',
                          borderBottom: '1px solid #e9ecef',
                          fontWeight: '600',
                          fontSize: '13px',
                          color: '#495057',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Associations
                        </div>
                        {associations.map((opt, idx) => (
                          <div
                            key={`assoc-${idx}`}
                            onClick={() => handleAddFromDropdown(opt)}
                            style={{
                              padding: '12px 15px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f0f0f0',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                            onMouseLeave={(e) => e.target.style.background = 'white'}
                          >
                            {opt.name}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Communities Section */}
                    {communities.length > 0 && (
                      <div>
                        <div style={{
                          padding: '10px 15px',
                          background: '#f8f9fa',
                          borderTop: associations.length > 0 ? '2px solid #e9ecef' : 'none',
                          borderBottom: '1px solid #e9ecef',
                          fontWeight: '600',
                          fontSize: '13px',
                          color: '#495057',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Communities
                        </div>
                        {communities.map((opt, idx) => (
                          <div
                            key={`comm-${idx}`}
                            onClick={() => handleAddFromDropdown(opt)}
                            style={{
                              padding: '12px 15px',
                              cursor: 'pointer',
                              borderBottom: idx < communities.length - 1 ? '1px solid #f0f0f0' : 'none',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                            onMouseLeave={(e) => e.target.style.background = 'white'}
                          >
                            {opt.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowManualForm(!showManualForm)}
            style={{
              padding: '10px 15px',
              background: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              whiteSpace: 'nowrap'
            }}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Manually
          </button>
        </div>
      </div>

      {showManualForm && (
        <div style={{
          padding: '15px',
          border: '1px solid #ddd',
          borderRadius: '6px',
          marginBottom: '10px',
          background: '#f9f9f9'
        }}>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              Name <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              value={manualInput.name}
              onChange={(e) => setManualInput({ ...manualInput, name: e.target.value })}
              placeholder="Enter community/association name"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Type</label>
              <select
                value={manualInput.type}
                onChange={(e) => setManualInput({ ...manualInput, type: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              >
                <option value="community">Community</option>
                <option value="association">Association</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Country</label>
              <select
                value={manualInput.country}
                onChange={(e) => setManualInput({ ...manualInput, country: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              >
                <option value="">Select country</option>
                {COMMUNITY_COUNTRIES.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={handleAddManual}
              disabled={!manualInput.name.trim()}
              style={{
                padding: '8px 15px',
                background: manualInput.name.trim() ? '#27ae60' : '#bdc3c7',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: manualInput.name.trim() ? 'pointer' : 'not-allowed'
              }}
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => {
                setShowManualForm(false);
                setManualInput({ name: '', type: 'community', country: '' });
              }}
              style={{
                padding: '8px 15px',
                background: '#95a5a6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {selectedItems.length > 0 && (
        <div style={{ marginTop: '15px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Selected Communities/Associations:
          </label>
          {selectedItems.map((item, index) => (
            <div
              key={index}
              style={{
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                marginBottom: '8px',
                background: 'white',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong>{item.name}</strong>
                  <span style={{
                    marginLeft: '10px',
                    padding: '2px 8px',
                    background: item.type === 'association' ? '#e3f2fd' : '#f3e5f5',
                    color: item.type === 'association' ? '#1976d2' : '#7b1fa2',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    {item.type === 'association' ? 'Association' : 'Community'}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#e74c3c',
                    cursor: 'pointer',
                    fontSize: '16px',
                    padding: '4px 8px'
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                  Country/Location:
                </label>
                <select
                  value={item.country || ''}
                  onChange={(e) => handleUpdateCountry(index, e.target.value)}
                  style={{
                    width: '100%',
                    padding: '6px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '13px'
                  }}
                >
                  <option value="">Select country</option>
                  {COMMUNITY_COUNTRIES.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Argentina", "Armenia", "Australia",
  "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium",
  "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei",
  "Bulgaria", "Burkina Faso", "Burundi", "Cambodia", "Cameroon", "Canada", "Cape Verde",
  "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica",
  "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Djibouti", "Dominica", "Dominican Republic",
  "East Timor", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia",
  "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana",
  "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Honduras",
  "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy",
  "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati", "North Korea", "South Korea",
  "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya",
  "Liechtenstein", "Lithuania", "Luxembourg", "Madagascar", "Malawi", "Malaysia", "Maldives",
  "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia",
  "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia",
  "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "Norway",
  "Oman", "Pakistan", "Palau", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines",
  "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis",
  "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Saudi Arabia",
  "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia",
  "Solomon Islands", "Somalia", "South Africa", "South Sudan", "Spain", "Sri Lanka", "Sudan",
  "Suriname", "Swaziland", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania",
  "Thailand", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan",
  "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States",
  "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela", "Vietnam", "Yemen",
  "Zambia", "Zimbabwe"
];

const EmployerMyProfile = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const { id: viewUserId } = useParams();
  const [viewingUser, setViewingUser] = useState(null);
  const isViewingOtherProfile = !!viewUserId && viewUserId !== currentUser?._id && viewUserId !== currentUser?.id;
  const viewerRole = (currentUser?.role || '').toLowerCase();
  const user = isViewingOtherProfile ? viewingUser : currentUser;
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingSections, setEditingSections] = useState({});
  const [profileCompletionPercentage, setProfileCompletionPercentage] = useState(0);
  const [logoPreview, setLogoPreview] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [selectedPhotoFile, setSelectedPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isMobileView, setIsMobileView] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768;
  });
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const profilePhotoInputRef = useRef(null);
  
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  const hasAssociationVerification = useMemo(() => {
    const cached = sessionStorage.getItem('myProfileData');
    if (!cached) return false;
    try {
      const data = JSON.parse(cached);
      return !!(data.hasAssociationVerification || (Array.isArray(data.verifiedByAssociations) && data.verifiedByAssociations.length > 0));
    } catch {
      return false;
    }
  }, []);
  const associationBadgeLabel = useMemo(() => {
    const cached = sessionStorage.getItem('myProfileData');
    if (!cached) return undefined;
    try {
      const data = JSON.parse(cached);
      const assocNameRaw =
        data.associationName || data.organisation_name || data.organisationName || '';
      const assocName = (assocNameRaw || '').toString().trim();
      if (!assocName) return undefined;
      if (assocName.toLowerCase().includes('united asian network') || assocName.toLowerCase().includes('uan')) {
        return 'UAN Verified';
      }
      if (assocName.toLowerCase().includes('baps')) {
        return 'BAPS Verified';
      }
      return `${assocName} Verified`;
    } catch {
      return undefined;
    }
  }, []);
  
  const [profileForm, setProfileForm] = useState({
    // Company Information
    companyName: '',
    companyEmail: '',
    companyPhone: '',
    companyWebsite: '',
    companySize: '',
    yearFounded: '',
    industry: '',
    companyDescription: '',
    companyLogo: '',
    showLogoOnWebsite: true,
    
    // Company Location
    country: '',
    state: '',
    city: '',
    postalCode: '',
    address: '',
    latitude: '',
    longitude: '',
    
    // Employer Personal Details
    firstName: '',
    lastName: '',
    jobTitle: '',
    recruiterPhone: '',
    recruiterEmail: '',
    linkedinProfile: '',
    communitiesAssociations: [],
    
    // Recruitment Specialization (employer-specific)
    industries: [],
    functions: [],
    hiringVolume: '',
    
    // Services & Offerings
    employmentTypes: [],
    
    // Social Media
    linkedinCompany: '',
    facebook: '',
    twitter: '',
    instagram: '',
    additionalLinks: [],
    
    // Additional
    taxId: '',
    referralSource: '',
    additionalComments: '',
    participateInVolunteering: '',
    volunteeringDetails: '',
    isAssociationVerified: false
  });

  // Define getPrimaryLocationDisplay before it's used
  const getPrimaryLocationDisplay = () => {
    const city = profileForm.city || '';
    const country = profileForm.country || '';
    const combined = `${city}, ${country}`.replace(/^,\s*|,\s*$/g, '').trim();
    if (combined) return combined;
    return profileForm.address || '—';
  };

  const mapQuery = (() => {
    const parts = [
      profileForm.address,
      profileForm.city,
      profileForm.state,
      profileForm.country,
      profileForm.postalCode,
    ]
      .map(value => (value || '').toString().trim())
      .filter(Boolean);

    if (parts.length > 0) {
      const uniqueParts = [];
      parts.forEach(part => {
        if (!uniqueParts.includes(part)) {
          uniqueParts.push(part);
        }
      });
      return uniqueParts.join(', ');
    }

    const fallback = getPrimaryLocationDisplay();
    return fallback === '—' ? '' : fallback;
  })();

  // Input states for arrays
  const [industryInput, setIndustryInput] = useState('');
  const [functionInput, setFunctionInput] = useState('');
  const [linkTypeInput, setLinkTypeInput] = useState('');
  const [linkUrlInput, setLinkUrlInput] = useState('');
  const [employmentTypeInput, setEmploymentTypeInput] = useState('');

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };

    if (typeof window !== 'undefined') {
      handleResize();
      window.addEventListener('resize', handleResize);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  useEffect(() => {
    if (isMobileView) {
      setMobileSidebarOpen(false);
    } else {
      setMobileSidebarOpen(true);
    }
  }, [isMobileView]);

  const getMapsUrl = () => {
    const displayLocation = getPrimaryLocationDisplay();
    const lat = profileForm.latitude ? parseFloat(profileForm.latitude) : null;
    const lng = profileForm.longitude ? parseFloat(profileForm.longitude) : null;
    let mapsUrl = 'https://www.google.com/maps/search/?api=1';

    if (!Number.isNaN(lat) && !Number.isNaN(lng) && lat !== null && lng !== null) {
      mapsUrl += `&query=${encodeURIComponent(`${lat},${lng}`)}`;
      return mapsUrl;
    } else if (displayLocation && displayLocation !== '—') {
      mapsUrl += `&query=${encodeURIComponent(displayLocation)}`;
      return mapsUrl;
    }

    return null;
  };

  // Fetch another user's profile when viewing
  useEffect(() => {
    const loadOtherUserProfile = async () => {
      if (!viewUserId || !isViewingOtherProfile) return;
      
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        const response = await axios.get(
          buildApiUrl(`/api/users/profile/${viewUserId}`),
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        
        if (response.data) {
          const userData = response.data;
          setViewingUser(userData);
          
          const ensureArray = (value) => {
            if (!value) return [];
            if (Array.isArray(value)) return value;
            if (typeof value === 'string') {
              try {
                const parsed = JSON.parse(value);
                return Array.isArray(parsed) ? parsed : [];
              } catch {
                return value.split(',').map(item => item.trim()).filter(Boolean);
              }
            }
            return [];
          };
          
          setProfileForm({
            companyName: userData.companyName || '',
            companyEmail: userData.companyEmail || userData.email || '',
            companyPhone: userData.companyPhone || userData.phoneNumber || '',
            companyWebsite: userData.companyWebsite || '',
            companySize: userData.companySize || '',
            yearFounded: userData.yearFounded || '',
            industry: userData.industry || '',
            companyDescription: userData.companyDescription || '',
            companyLogo: userData.companyLogo || '',
            showLogoOnWebsite: typeof userData.showLogoOnWebsite === 'boolean' ? userData.showLogoOnWebsite : true,
            country: userData.country || '',
            state: userData.state || '',
            city: userData.city || '',
            postalCode: userData.postalCode || '',
            address: userData.address || '',
            latitude: userData.latitude || '',
            longitude: userData.longitude || '',
            firstName: userData.firstName || '',
            lastName: userData.lastName || '',
            jobTitle: userData.jobTitle || '',
            recruiterPhone: userData.recruiterPhone || userData.phoneNumber || '',
            recruiterEmail: userData.recruiterEmail || userData.email || '',
            linkedinProfile: userData.linkedinProfile || '',
            communitiesAssociations: (() => {
              // Parse communitiesAssociations (could be string or array)
              const value = userData.communitiesAssociations;
              if (!value) return [];
              if (Array.isArray(value)) return value;
              if (typeof value === 'string') {
                try {
                  const parsed = JSON.parse(value);
                  return Array.isArray(parsed) ? parsed : [];
                } catch {
                  return [];
                }
              }
              return [];
            })(),
            industries: ensureArray(userData.industries).length > 0 
              ? ensureArray(userData.industries) 
              : (userData.industry ? [userData.industry] : []),
            functions: ensureArray(userData.functions),
            hiringVolume: userData.hiringVolume || '',
            employmentTypes: ensureArray(userData.employmentTypes),
            linkedinCompany: userData.linkedinCompany || '',
            facebook: userData.facebook || '',
            twitter: userData.twitter || '',
            instagram: userData.instagram || '',
            additionalLinks: ensureArray(userData.additionalLinks),
            taxId: userData.taxId || '',
            referralSource: userData.referralSource || '',
            additionalComments: userData.additionalComments || '',
            participateInVolunteering: userData.participateInVolunteering || '',
            volunteeringDetails: userData.volunteeringDetails || '',
            isAssociationVerified: userData.isAssociationVerified || (userData.verifiedByAssociations && userData.verifiedByAssociations.length > 0) || false
          });
          
          if (userData.companyLogo) {
            setLogoPreview(userData.companyLogo);
          }
          
          // Load profile photo if available (for viewing other user's profile)
          const photoPath = userData.profileImage || userData.profilePhotoPath || userData.profilePhoto;
          if (photoPath) {
            let normalizedPath = photoPath.replace(/\\/g, '/');
            if (normalizedPath.startsWith('uploads/')) {
              const uploadPath = normalizedPath.replace('uploads/', '');
              const fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
              setPhotoPreview(fullUrl);
            } else if (normalizedPath.startsWith('/uploads/')) {
              const uploadPath = normalizedPath.replace('/uploads/', '');
              const fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
              setPhotoPreview(fullUrl);
            } else if (normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://')) {
              setPhotoPreview(normalizedPath);
            } else {
              const fullUrl = buildApiUrl(`/api/users/uploads/${normalizedPath}`);
              setPhotoPreview(fullUrl);
            }
          }
          
          // Use backend's profileCompletion if available, otherwise calculate locally
          const backendCompletion = userData.profileCompletion;
          if (backendCompletion !== undefined && backendCompletion !== null) {
            console.log(`✅ Using BACKEND profileCompletion for viewed user: ${backendCompletion}%`);
            setProfileCompletionPercentage(backendCompletion);
          } else {
            console.log('⚠️ No backend profileCompletion for viewed user, calculating locally...');
            calculateProfileCompletion(userData);
          }
          
          // Initialize map with location if available
          if (userData.latitude && userData.longitude) {
            setTimeout(() => {
              if (mapInstanceRef.current) {
                const lat = parseFloat(userData.latitude);
                const lng = parseFloat(userData.longitude);
                safeLeafletSetView(mapInstanceRef.current, [lat, lng], 13);
                if (markerRef.current) {
                  markerRef.current.setLatLng([lat, lng]);
                } else {
                  setMarker(lat, lng);
                }
              }
            }, 500);
          }
        }
      } catch (error) {
        console.error('Error loading other user profile:', error);
      } finally {
        setLoading(false);
      }
    };

    loadOtherUserProfile();
  }, [viewUserId, isViewingOtherProfile]);

  // Fetch current user profile - defined at component level so it can be reused
  const loadProfile = useCallback(async () => {
    if (isViewingOtherProfile) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        buildApiUrl('/api/user/profile'),
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.data) {
        const userData = response.data;
          
          console.log('📋 Employer Profile Data Received:', userData);
          console.log('🏢 Company Name:', userData.companyName);
          console.log('📧 Company Email:', userData.companyEmail);
          console.log('👤 First Name:', userData.firstName);
          console.log('📍 City:', userData.city);
          console.log('🔍 Full Response Data:', JSON.stringify(userData, null, 2));
          
          // Check if data is actually empty
          const hasData = userData.companyName || userData.firstName || userData.city || userData.country;
          if (!hasData) {
            console.error('⚠️ WARNING: Profile data appears to be empty!');
            console.error('   This could indicate:');
            console.error('   1. Data was not saved to database');
            console.error('   2. User ID mismatch');
            console.error('   3. Data was deleted');
            console.error('   User ID from token:', currentUser?._id || currentUser?.id);
          }
          
          const ensureArray = (value) => {
            if (!value) return [];
            if (Array.isArray(value)) return value;
            if (typeof value === 'string') {
              try {
                const parsed = JSON.parse(value);
                return Array.isArray(parsed) ? parsed : [];
              } catch {
                return value.split(',').map(item => item.trim()).filter(Boolean);
              }
            }
            return [];
          };

          // Helper to parse communitiesAssociations (could be string or array)
          const parseCommunitiesAssociations = (value) => {
            if (!value) return [];
            if (Array.isArray(value)) return value;
            if (typeof value === 'string') {
              try {
                const parsed = JSON.parse(value);
                return Array.isArray(parsed) ? parsed : [];
              } catch {
                return [];
              }
            }
            return [];
          };
          
          setProfileForm({
            companyName: userData.companyName || '',
            companyEmail: userData.companyEmail || userData.email || '',
            companyPhone: userData.companyPhone || userData.phoneNumber || '',
            companyWebsite: userData.companyWebsite || '',
            companySize: userData.companySize || '',
            yearFounded: userData.yearFounded || '',
            industry: userData.industry || '',
            companyDescription: userData.companyDescription || '',
            companyLogo: userData.companyLogo || '',
            showLogoOnWebsite: typeof userData.showLogoOnWebsite === 'boolean' ? userData.showLogoOnWebsite : true,
            country: userData.country || '',
            state: userData.state || '',
            city: userData.city || '',
            postalCode: userData.postalCode || '',
            address: userData.address || '',
            latitude: userData.latitude || '',
            longitude: userData.longitude || '',
            firstName: userData.firstName || '',
            lastName: userData.lastName || '',
            jobTitle: userData.jobTitle || '',
            recruiterPhone: userData.recruiterPhone || userData.phoneNumber || '',
            recruiterEmail: userData.recruiterEmail || userData.email || '',
            linkedinProfile: userData.linkedinProfile || '',
            communitiesAssociations: parseCommunitiesAssociations(userData.communitiesAssociations),
            industries: ensureArray(userData.industries).length > 0 
              ? ensureArray(userData.industries) 
              : (userData.industry ? [userData.industry] : []),
            functions: ensureArray(userData.functions),
            hiringVolume: userData.hiringVolume || '',
            employmentTypes: ensureArray(userData.employmentTypes),
            linkedinCompany: userData.linkedinCompany || '',
            facebook: userData.facebook || '',
            twitter: userData.twitter || '',
            instagram: userData.instagram || '',
            additionalLinks: ensureArray(userData.additionalLinks),
            taxId: userData.taxId || '',
            referralSource: userData.referralSource || '',
            additionalComments: userData.additionalComments || '',
            participateInVolunteering: userData.participateInVolunteering || '',
            volunteeringDetails: userData.volunteeringDetails || '',
            isAssociationVerified: userData.isAssociationVerified || (userData.verifiedByAssociations && userData.verifiedByAssociations.length > 0) || false
          });
          
          if (userData.companyLogo) {
            setLogoPreview(userData.companyLogo);
          }
          
          // Load profile photo if available
          const photoPath = userData.profileImage || userData.profilePhotoPath || userData.profilePhoto;
          if (photoPath) {
            let normalizedPath = photoPath.replace(/\\/g, '/');
            if (normalizedPath.startsWith('uploads/')) {
              const uploadPath = normalizedPath.replace('uploads/', '');
              const fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
              setPhotoPreview(fullUrl);
            } else if (normalizedPath.startsWith('/uploads/')) {
              const uploadPath = normalizedPath.replace('/uploads/', '');
              const fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
              setPhotoPreview(fullUrl);
            } else if (normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://')) {
              setPhotoPreview(normalizedPath);
            } else {
              const fullUrl = buildApiUrl(`/api/users/uploads/${normalizedPath}`);
              setPhotoPreview(fullUrl);
            }
          }
          
          console.log('✅ Profile Form State Set');
          console.log('   - Company:', userData.companyName);
          console.log('   - First Name:', userData.firstName);
          console.log('   - Industries:', userData.industries);
          console.log('   - Functions:', userData.functions);
          
          // Force a re-render by updating a dummy state if needed
          // The state update above should trigger a re-render automatically
          
          // Use backend's profileCompletion if available, otherwise calculate locally
          const backendCompletion = userData.profileCompletion;
          if (backendCompletion !== undefined && backendCompletion !== null) {
            console.log(`✅ Using BACKEND profileCompletion: ${backendCompletion}%`);
            setProfileCompletionPercentage(backendCompletion);
          } else {
            console.log('⚠️ No backend profileCompletion, calculating locally...');
            calculateProfileCompletion(userData);
          }
          
          // Initialize map with location if available
          if (userData.latitude && userData.longitude) {
            setTimeout(() => {
              if (mapInstanceRef.current) {
                const lat = parseFloat(userData.latitude);
                const lng = parseFloat(userData.longitude);
                safeLeafletSetView(mapInstanceRef.current, [lat, lng], 13);
                if (markerRef.current) {
                  markerRef.current.setLatLng([lat, lng]);
                } else {
                  setMarker(lat, lng);
                }
              }
            }, 500);
          }
        }
      } catch (error) {
        console.error('❌ Error loading profile:', error);
        console.error('   Error details:', error.response?.data || error.message);
        console.error('   Status:', error.response?.status);
        console.error('   User ID:', currentUser?._id || currentUser?.id);
        
        // Show user-friendly error message
        if (error.response?.status === 404) {
          alert('Profile not found. Please complete your profile registration.');
        } else if (error.response?.status === 401) {
          alert('Session expired. Please log in again.');
        } else {
          alert('Failed to load profile. Please refresh the page or contact support.');
        }
      } finally {
        setLoading(false);
      }
  }, [isViewingOtherProfile]);

  // Call loadProfile on mount
  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // Debug: Log when profileForm changes
  useEffect(() => {
    console.log('🔄 profileForm state changed:');
    console.log('   - companyName:', profileForm.companyName);
    console.log('   - firstName:', profileForm.firstName);
    console.log('   - city:', profileForm.city);
    console.log('   - industries:', profileForm.industries);
  }, [profileForm.companyName, profileForm.firstName, profileForm.city, profileForm.industries]);

  const calculateProfileCompletion = (data) => {
    const requiredFields = [
      data.companyName, data.companyEmail, data.companyPhone,
      data.companySize, data.yearFounded, data.industry,
      data.companyDescription, data.country, data.state,
      data.city, data.address, data.firstName, data.lastName,
      data.jobTitle, data.recruiterPhone, data.recruiterEmail
    ];
    
    const filledCount = requiredFields.filter(field => field && field.toString().trim() !== '').length;
    let arrayBonus = 0;
    if (data.industries && data.industries.length > 0) arrayBonus++;
    if (data.functions && data.functions.length > 0) arrayBonus++;
    if (data.employmentTypes && data.employmentTypes.length > 0) arrayBonus++;
    
    const total = requiredFields.length + 3; // 16 fields + 3 arrays
    const filled = filledCount + arrayBonus;
    const percentage = Math.round((filled / total) * 100);
    
    setProfileCompletionPercentage(percentage);
  };

  // Initialize map
  useEffect(() => {
    const initMap = () => {
      if (mapRef.current && !mapInstanceRef.current) {
        const map = L.map(mapRef.current, {
          center: [-1.286389, 36.817223],
          zoom: 12,
        });
        
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors',
          maxZoom: 19
        }).addTo(map);

        mapInstanceRef.current = map;

        map.on('click', function(e) {
          if (editingSections.location) {
            setMarker(e.latlng.lat, e.latlng.lng);
          }
        });

        setMapLoaded(true);
      }
    };

    const timer = setTimeout(initMap, 100);
    return () => {
      clearTimeout(timer);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [editingSections.location]);

  const setMarker = useCallback((lat, lng) => {
    const map = mapInstanceRef.current;
    if (!map) return;

    if (markerRef.current) {
      map.removeLayer(markerRef.current);
    }
    
    const marker = L.marker([lat, lng], {
      draggable: true
    }).addTo(map);

    markerRef.current = marker;

    setProfileForm(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng
    }));

    marker.on('dragend', function(e) {
      const position = marker.getLatLng();
      setProfileForm(prev => ({
        ...prev,
        latitude: position.lat,
        longitude: position.lng
      }));
    });
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setProfileForm(prev => ({...prev, [name]: checked}));
    } else {
      setProfileForm(prev => ({...prev, [name]: value}));
    }
  };

  const [companyLogoFile, setCompanyLogoFile] = useState(null);
  const companyLogoInputRef = useRef(null);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        e.target.value = '';
        return;
      }
      if (!file.type.match(/^image\/(png|jpg|jpeg)$/i)) {
        alert('Only PNG and JPG images are allowed');
        e.target.value = '';
        return;
      }
      setCompanyLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (50KB limit like intern/jobseeker)
      if (file.size > 50 * 1024) {
        alert('Profile photo must be 50KB or smaller. Please compress your image.');
        e.target.value = '';
        return;
      }
      
      setSelectedPhotoFile(file);
      // Create preview immediately
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Auto-upload the photo immediately (like intern/jobseeker does)
      try {
        setSaving(true);
        console.log('📸 Auto-uploading profile photo immediately...');
        
        const formData = new FormData();
        formData.append('profilePhoto', file);
        
        // Include minimal profile data to ensure the save works
        const minimalPayload = {
          firstName: profileForm.firstName,
          lastName: profileForm.lastName
        };
        
        // Append all form fields as JSON strings for complex data
        Object.keys(minimalPayload).forEach(key => {
          const value = minimalPayload[key];
          if (value !== null && value !== undefined) {
            if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
              formData.append(key, JSON.stringify(value));
            } else {
              formData.append(key, value);
            }
          }
        });
        
        const token = localStorage.getItem('token');
        const response = await axios.put(buildApiUrl('/api/user/profile'), formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (response.status === 200) {
          console.log('✅ Photo uploaded successfully:', response.data);
          
          // Update profileForm with the new photo path if returned
          if (response.data.profileImage || response.data.profilePhotoPath) {
            const photoPath = response.data.profileImage || response.data.profilePhotoPath;
            console.log('📸 New photo path from server:', photoPath);
            if (photoPath && typeof photoPath === 'string') {
              let normalizedPath = photoPath.replace(/\\/g, '/');
              if (normalizedPath.startsWith('uploads/')) {
                const uploadPath = normalizedPath.replace('uploads/', '');
                const fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
                console.log('📸 Setting profilePhoto to:', fullUrl);
                setPhotoPreview(fullUrl);
              } else if (normalizedPath.startsWith('/uploads/')) {
                const uploadPath = normalizedPath.replace('/uploads/', '');
                const fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
                console.log('📸 Setting profilePhoto to:', fullUrl);
                setPhotoPreview(fullUrl);
              } else if (normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://')) {
                console.log('📸 Setting profilePhoto to full URL:', normalizedPath);
                setPhotoPreview(normalizedPath);
              } else {
                const fullUrl = buildApiUrl(`/api/users/uploads/${normalizedPath}`);
                console.log('📸 Setting profilePhoto to:', fullUrl);
                setPhotoPreview(fullUrl);
              }
            }
          }
          
          // Clear the file selection after successful upload
          setSelectedPhotoFile(null);
          if (e.target) {
            e.target.value = '';
          }
          
          alert('✓ Profile photo uploaded successfully!');
        }
      } catch (error) {
        console.error('❌ Error uploading photo:', error);
        console.error('Error details:', error.response?.data);
        alert('❌ Error uploading photo. Please try again.');
      } finally {
        setSaving(false);
      }
    }
  };

  const addIndustry = () => {
    if (industryInput && !profileForm.industries.includes(industryInput)) {
      setProfileForm(prev => ({
        ...prev,
        industries: [...prev.industries, industryInput]
      }));
      setIndustryInput('');
    }
  };

  const addEmploymentType = () => {
    if (employmentTypeInput && !profileForm.employmentTypes.includes(employmentTypeInput)) {
      setProfileForm(prev => ({
        ...prev,
        employmentTypes: [...prev.employmentTypes, employmentTypeInput]
      }));
      setEmploymentTypeInput('');
    }
  };

  const removeEmploymentType = (index) => {
    setProfileForm(prev => ({
      ...prev,
      employmentTypes: prev.employmentTypes.filter((_, i) => i !== index)
    }));
  };

  const removeIndustry = (index) => {
    setProfileForm(prev => ({
      ...prev,
      industries: prev.industries.filter((_, i) => i !== index)
    }));
  };

  const addFunction = () => {
    if (functionInput && !profileForm.functions.includes(functionInput)) {
      setProfileForm(prev => ({
        ...prev,
        functions: [...prev.functions, functionInput]
      }));
      setFunctionInput('');
    }
  };

  const removeFunction = (index) => {
    setProfileForm(prev => ({
      ...prev,
      functions: prev.functions.filter((_, i) => i !== index)
    }));
  };

  const addLink = () => {
    if (!linkTypeInput.trim() || !linkUrlInput.trim()) {
      alert('Please enter both link type and URL');
      return;
    }

    try {
      new URL(linkUrlInput);
    } catch (e) {
      alert('Please enter a valid URL (including https://)');
      return;
    }

    setProfileForm(prev => ({
      ...prev,
      additionalLinks: [...prev.additionalLinks, { type: linkTypeInput, url: linkUrlInput }]
    }));
    setLinkTypeInput('');
    setLinkUrlInput('');
  };

  const removeLink = (index) => {
    setProfileForm(prev => ({
      ...prev,
      additionalLinks: prev.additionalLinks.filter((_, i) => i !== index)
    }));
  };

  const toggleSectionEdit = (section) => {
    setEditingSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const saveSection = async (section) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      let response;
      
      // If there's a logo file, use FormData
      if (companyLogoFile) {
        const formData = new FormData();
        formData.append('companyLogo', companyLogoFile);
        
        // Append all profile data
        Object.keys(profileForm).forEach(key => {
          const value = profileForm[key];
          // Include boolean false values explicitly
          if (value !== null && value !== undefined) {
            if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
              formData.append(key, JSON.stringify(value));
            } else if (typeof value === 'boolean') {
              // Explicitly handle boolean values
              formData.append(key, value.toString());
            } else {
              formData.append(key, value);
            }
          } else if (typeof value === 'boolean' && value === false) {
            // Explicitly save false boolean values
            formData.append(key, 'false');
          }
        });
        
        response = await axios.put(
          buildApiUrl('/api/recruiters/profile'),
          formData,
          { 
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            } 
          }
        );
        
        // Clear logo file after successful upload
        if (response.data) {
          const logoPath = response.data.companyLogo;
          if (logoPath) {
            let normalizedPath = logoPath.replace(/\\/g, '/');
            let fullUrl = '';
            if (normalizedPath.startsWith('uploads/')) {
              const uploadPath = normalizedPath.replace('uploads/', '');
              fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
            } else if (normalizedPath.startsWith('/uploads/')) {
              const uploadPath = normalizedPath.replace('/uploads/', '');
              fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
            } else if (normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://')) {
              fullUrl = normalizedPath;
            } else {
              fullUrl = buildApiUrl(`/api/users/uploads/${normalizedPath}`);
            }
            setProfileForm(prev => ({ ...prev, companyLogo: fullUrl }));
            setLogoPreview(fullUrl);
          }
          setCompanyLogoFile(null);
          if (companyLogoInputRef.current) {
            companyLogoInputRef.current.value = '';
          }
        }
      } else {
        // Regular JSON save
        response = await axios.put(
          buildApiUrl('/api/recruiters/profile'),
          profileForm,
          { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
      }
      
      // Update local state immediately if response includes updated values
      if (response.data) {
        const res = response.data;
        const userData = res.user || res;
        // Update showLogoOnWebsite from response if available
        if ('showLogoOnWebsite' in (userData || res)) {
          setProfileForm(prev => ({
            ...prev,
            showLogoOnWebsite: (userData || res).showLogoOnWebsite
          }));
        }
        
        // Update profile completion from response (API returns it inside user)
        const completion = userData?.profileCompletion ?? res.profileCompletion;
        if (completion !== undefined) {
          setProfileCompletionPercentage(completion);
        }
      }
      
      toggleSectionEdit(section);
      alert('Profile updated successfully!');
      
      // Reload profile data to ensure we have the latest values from backend
      // This ensures all fields are correctly synced
      await loadProfile();
    } catch (error) {
      console.error('Error saving profile:', error);
      const message = error.response?.data?.error || 'Failed to update profile. Please try again.';
      alert(message);
    } finally {
      setSaving(false);
    }
  };

  // Style definitions
  const containerStyle = { 
    padding: '0 24px 32px 24px', 
    maxWidth: '100%', 
    width: '100%', 
    margin: '0',
    minHeight: '100vh',
    boxSizing: 'border-box'
  };

  const elevatedCardStyle = { 
    border: '1px solid #edf2f7', 
    borderRadius: '16px', 
    boxShadow: '0 10px 30px rgba(17, 24, 39, 0.06)', 
    background: '#fff', 
    marginBottom: '20px',
    overflow: 'hidden'
  };

  const profileHeaderStyle = { 
    background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)', 
    color: '#fff', 
    padding: '16px 24px', 
    borderRadius: '16px', 
    marginBottom: '10px', 
    position: 'relative',
    overflow: 'hidden',
    minHeight: '120px',
    boxShadow: '0 10px 30px rgba(102, 126, 234, 0.25)'
  };

  const headerPatternStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.1,
    backgroundImage: `
      radial-gradient(circle at 20% 50%, rgba(255,255,255,0.2) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(255,255,255,0.2) 0%, transparent 50%)
    `
  };

  const profileAvatarStyle = {
    width: isMobileView ? '64px' : '76px',
    height: isMobileView ? '64px' : '76px',
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.25)',
    backdropFilter: 'blur(10px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: isMobileView ? '22px' : '30px',
    fontWeight: '700',
    color: '#ffffff',
    border: '3px solid rgba(255,255,255,0.4)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
    textShadow: '0 4px 8px rgba(0,0,0,0.5)',
    position: 'relative',
    overflow: 'hidden',
    flexShrink: 0
  };

  const gridStyle = { display: 'grid', gap: '15px' };
  
  const formInputBase = {
    width: '100%',
    padding: '12px',
    borderRadius: '8px',
    border: '2px solid #e5e7eb',
    fontSize: '14px',
    transition: 'all 0.3s ease',
    outline: 'none'
  };
  
  const getViewModeStyle = (section) => {
    return !editingSections[section]
      ? { ...formInputBase, background: '#f7f9fc', border: '2px solid #e6eaf1', color: '#1e293b', cursor: 'not-allowed' }
      : formInputBase;
  };

  // Resolve a logo URL (stored path or absolute) to a usable src
  const resolveLogoUrl = (raw) => {
    if (!raw) return '';
    if (/^https?:\/\//i.test(raw) || raw.startsWith('data:') || raw.startsWith('blob:')) return raw;
    const normalized = raw.replace(/\\/g, '/').replace(/^\/+/, '');
    if (normalized.startsWith('uploads/')) {
      return buildApiUrl(`/api/users/uploads/${normalized.replace(/^uploads\//, '')}`);
    }
    return buildApiUrl(`/api/users/uploads/${normalized}`);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f8fafc' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
          <p style={{ fontSize: '18px', color: '#64748b' }}>Loading profile...</p>
        </div>
      </div>
    );
  }

  const EditButton = ({ section }) => (
    isViewingOtherProfile ? null : (
    !editingSections[section] ? (
      <button 
        onClick={() => toggleSectionEdit(section)}
        style={{ 
          background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)', 
          color: 'white', 
          border: 'none',
          padding: '8px 16px',
          borderRadius: '6px',
          fontSize: '14px',
          fontWeight: '600',
          cursor: 'pointer',
          transition: 'all 0.3s ease'
        }}
      >
        <FontAwesomeIcon icon={faEdit} /> Edit
      </button>
    ) : (
      <>
        <button 
          onClick={() => saveSection(section)} 
          disabled={saving}
          style={{ 
            background: '#10b981', 
            color: 'white', 
            border: 'none',
            padding: '8px 16px',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: saving ? 'not-allowed' : 'pointer',
            marginRight: '8px'
          }}
        >
          <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
        </button>
        <button 
          onClick={() => toggleSectionEdit(section)}
          style={{
            background: '#dc2626',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          <FontAwesomeIcon icon={faTimes} /> Cancel
        </button>
      </>
    ))
  );

  const sidebarWidth = 280;
  const sidebarStyles = {
    width: isMobileView ? `${MOBILE_SIDEBAR_WIDTH}px` : `${sidebarWidth}px`,
    maxWidth: isMobileView ? '72vw' : undefined,
    minWidth: isMobileView ? 0 : undefined,
    flexShrink: 0,
    background: 'linear-gradient(180deg, #f97316 0%, #0d9488 100%)',
    padding: isMobileView ? '1rem 0.85rem' : '2rem 1.5rem',
    boxShadow: isMobileView ? '4px 0 20px rgba(15, 23, 42, 0.2)' : '4px 0 20px rgba(249, 115, 22, 0.15)',
    position: 'fixed',
    left: 0,
    top: isMobileView ? MOBILE_HEADER_HEIGHT : 0,
    height: isMobileView ? `calc(100vh - ${MOBILE_HEADER_HEIGHT}px)` : '100vh',
    overflowY: 'auto',
    overflowX: 'hidden',
    zIndex: isMobileView ? 1150 : 1000,
    color: '#ffffff',
    transform: isMobileView ? (mobileSidebarOpen ? 'translateX(0)' : 'translateX(-100%)') : 'translateX(0)',
    transition: 'transform 0.28s ease',
    pointerEvents: isMobileView && !mobileSidebarOpen ? 'none' : 'auto',
    boxSizing: 'border-box'
  };

  const mainContentStyles = {
    marginLeft: isMobileView ? 0 : `${sidebarWidth}px`,
    width: isMobileView ? '100%' : `calc(100% - ${sidebarWidth}px)`,
    maxWidth: isMobileView ? '100%' : `calc(100% - ${sidebarWidth}px)`,
    padding: isMobileView ? '0' : '40px',
    overflowX: 'hidden',
    overflowY: 'auto',
    minHeight: isMobileView ? 'auto' : '100vh',
    boxSizing: 'border-box',
    backgroundColor: '#f8fafc',
    flex: '1 1 auto',
    minWidth: 0
  };

  const responsiveContainerStyle = {
    ...containerStyle,
    padding: isMobileView ? '12px 12px 72px 12px' : containerStyle.padding,
    maxWidth: '100%',
    width: '100%',
    overflowX: 'hidden'
  };

  const grid2Style = {
    ...gridStyle,
    gridTemplateColumns: isMobileView ? '1fr' : 'repeat(2, 1fr)',
    marginBottom: '15px'
  };

  const handleSidebarNavClick = (e) => {
    if (isMobileView && e.target.closest('button')) {
      setTimeout(() => setMobileSidebarOpen(false), 200);
    }
  };

  const handleMobileBack = () => {
    if (isViewingOtherProfile) {
      if (viewerRole === 'employer') {
        navigate('/employer-dashboard');
      } else if (viewerRole === 'recruiter') {
        navigate('/recruiter-dashboard');
      } else {
        navigate('/admin?view=profile-browser');
      }
    } else {
      navigate('/employer-dashboard');
    }
  };

  const openLocationInMaps = () => {
    const mapsUrl = getMapsUrl();
    if (mapsUrl) {
      window.open(mapsUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const hasProfilePhoto = Boolean(photoPreview || profileForm.companyLogo);
  const profilePhotoSrc = photoPreview || resolveLogoUrl(profileForm.companyLogo);
  const profileInitials = `${profileForm.firstName?.[0] || 'E'}${profileForm.lastName?.[0] || ''}`;

  return (
    <>
      <style>{`
        .employer-my-profile-page .form-label {
          display: block;
          font-size: 14px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 6px;
        }
        
        .employer-my-profile-page .form-group {
          margin-bottom: 0;
        }
        
        .employer-my-profile-page input:focus,
        .employer-my-profile-page textarea:focus,
        .employer-my-profile-page select:focus {
          border-color: #f97316 !important;
          box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1) !important;
        }
        
        .employer-my-profile-page .tag {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: linear-gradient(135deg, #f97316 0%, #0d9488 100%);
          color: white;
          padding: 8px 14px;
          borderRadius: 20px;
          margin: 5px;
          fontSize: 14px;
          fontWeight: 500;
        }
        
        .employer-my-profile-page .tag .remove {
          cursor: pointer;
          font-weight: 700;
          transition: transform 0.2s ease;
        }
        
        .employer-my-profile-page .tag .remove:hover {
          transform: scale(1.2);
        }
        
        .employer-my-profile-page .required {
          color: #dc2626;
          margin-left: 4px;
        }
        
        .employer-my-profile-page .checkbox-group {
          margin-bottom: 12px;
          display: flex;
          align-items: center;
        }
        
        .employer-my-profile-page .checkbox-group input[type="checkbox"] {
          margin-right: 10px;
          width: 18px;
          height: 18px;
          cursor: pointer;
        }
        
        .employer-my-profile-page .checkbox-group label {
          cursor: pointer;
          font-size: 14px;
          color: #374151;
        }
        
        .employer-my-profile-page .info-badge {
          background: #eff6ff;
          color: #1e40af;
          padding: 8px 12px;
          borderRadius: 6px;
          fontSize: 13px;
          margin-bottom: 10px;
          border: 1px solid #bfdbfe;
        }
        
        .employer-my-profile-page .add-btn {
          background: linear-gradient(135deg, #f97316 0%, #0d9488 100%);
          color: white;
          border: none;
          padding: 10px 20px;
          borderRadius: 6px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .employer-my-profile-page .add-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(249, 115, 22, 0.3);
        }
        
        .employer-my-profile-page .skills-input-container {
          display: flex;
          gap: 10px;
          margin-bottom: 15px;
        }
        
        .employer-my-profile-page .skills-input-container input,
        .employer-my-profile-page .skills-input-container select {
          flex: 1;
          padding: 12px;
          borderRadius: 8px;
          border: 2px solid #e5e7eb;
          fontSize: 14px;
        }
        
        .employer-my-profile-page .link-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px;
          background: #f8fafc;
          borderRadius: 8px;
          margin-bottom: 10px;
          border: 1px solid #e2e8f0;
        }
        
        .employer-my-profile-page .link-item-content {
          flex: 1;
        }
        
        .employer-my-profile-page .link-item-type {
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 4px;
        }
        
        .employer-my-profile-page .link-item-url a {
          color: #3b82f6;
          text-decoration: none;
          font-size: 13px;
        }
        
        .employer-my-profile-page .remove-item-btn {
          background: #ef4444;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .employer-my-profile-page .remove-item-btn:hover {
          background: #dc2626;
        }
        
        .employer-my-profile-page .file-upload-label {
          display: flex;
          align-items: center;
          gap: 15px;
          padding: 20px;
          background: #f8fafc;
          border: 2px dashed #cbd5e1;
          borderRadius: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .employer-my-profile-page .file-upload-label:hover {
          border-color: #f97316;
          background: #fff5f0;
        }
        
        .employer-my-profile-page .file-upload-label input[type="file"] {
          display: none;
        }
        
        .employer-my-profile-page .logo-preview {
          max-width: 200px;
          max-height: 200px;
          border-radius: 12px;
          margin-bottom: 15px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }
        
        .employer-my-profile-page .map-container {
          border-radius: 12px;
          overflow: hidden;
          border: 2px solid #e2e8f0;
          position: relative;
        }
        
        .employer-my-profile-page .coordinates-display {
          margin-top: 10px;
          padding: 10px;
          background: #f0f9ff;
          border-radius: 6px;
          font-size: 13px;
          color: #0369a1;
          border: 1px solid #bae6fd;
        }
      `}</style>
      <div
        className={`employer-my-profile-page${isMobileView && mobileSidebarOpen ? ' js-profile-drawer-open' : ''}`}
        style={{
          display: 'flex',
          flexDirection: isMobileView ? 'column' : 'row',
          minHeight: '100vh',
          width: '100%',
          backgroundColor: '#f8fafc',
          overflowX: 'hidden'
        }}
      >
      {isMobileView && (
        <header className="js-profile-mobile-header">
          <div className="js-profile-header-start">
            <button
              type="button"
              className="js-profile-menu-btn btn-icon"
              onClick={() => setMobileSidebarOpen((prev) => !prev)}
              aria-label={mobileSidebarOpen ? 'Close profile navigation' : 'Open profile navigation'}
            >
              <FontAwesomeIcon icon={mobileSidebarOpen ? faTimes : faBars} />
            </button>
          </div>
          <h1 className="js-profile-mobile-title">My Profile</h1>
          <div className="js-profile-header-end">
            <button
              type="button"
              className="js-profile-back-btn"
              onClick={handleMobileBack}
            >
              {isViewingOtherProfile ? 'Back' : 'Dashboard'}
            </button>
          </div>
        </header>
      )}
      {isMobileView && mobileSidebarOpen && (
        <div
          className="js-profile-drawer-overlay"
          style={{
            top: MOBILE_HEADER_HEIGHT,
            left: `min(${MOBILE_SIDEBAR_WIDTH}px, 72vw)`
          }}
          onClick={() => setMobileSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      {/* Sidebar */}
      <div
        style={sidebarStyles}
        className="sidebar-container js-profile-drawer"
        onClick={handleSidebarNavClick}
      >
        {!isMobileView && (
        <div className="js-profile-drawer-intro" style={{ marginBottom: '2rem' }}>
          <h2 style={{ 
            color: '#ffffff', 
            fontSize: '1.5rem', 
            fontWeight: '800', 
            marginBottom: '0.5rem',
            textShadow: '0 4px 8px rgba(0,0,0,0.7)',
            letterSpacing: '0.5px'
          }}>
            My Profile
          </h2>
          <p style={{ 
            color: '#ffffff', 
            fontSize: '0.9rem',
            margin: 0,
            fontWeight: '700',
            textShadow: '0 2px 4px rgba(0,0,0,0.6)'
          }}>
            Manage your employer information
          </p>
        </div>
        )}
        
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ 
            color: '#ffffff', 
            fontSize: '1.1rem', 
            fontWeight: '700', 
            marginBottom: '1rem',
            textShadow: '0 3px 6px rgba(0,0,0,0.7)',
            letterSpacing: '0.3px'
          }}>
            Quick Navigation
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <button 
              onClick={() => document.querySelector('[data-section="company"]')?.scrollIntoView({ behavior: 'smooth' })}
              style={{
                background: 'rgba(0,0,0,0.3)',
                color: 'white',
                border: '2px solid rgba(255,255,255,0.3)',
                padding: '10px 15px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                textAlign: 'left',
                transition: 'all 0.3s ease'
              }}
            >
              <FontAwesomeIcon icon={faBuilding} /> Company Info
            </button>
            <button 
              onClick={() => document.querySelector('[data-section="location"]')?.scrollIntoView({ behavior: 'smooth' })}
              style={{
                background: 'rgba(0,0,0,0.3)',
                color: 'white',
                border: '2px solid rgba(255,255,255,0.3)',
                padding: '10px 15px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                textAlign: 'left',
                transition: 'all 0.3s ease'
              }}
            >
              <FontAwesomeIcon icon={faMapMarkerAlt} /> Location
            </button>
            <button 
              onClick={() => document.querySelector('[data-section="personal"]')?.scrollIntoView({ behavior: 'smooth' })}
              style={{
                background: 'rgba(0,0,0,0.3)',
                color: 'white',
                border: '2px solid rgba(255,255,255,0.3)',
                padding: '10px 15px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                textAlign: 'left',
                transition: 'all 0.3s ease'
              }}
            >
              <FontAwesomeIcon icon={faUserTie} /> Personal Details
            </button>
            <button 
              onClick={() => document.querySelector('[data-section="specialization"]')?.scrollIntoView({ behavior: 'smooth' })}
              style={{
                background: 'rgba(0,0,0,0.3)',
                color: 'white',
                border: '2px solid rgba(255,255,255,0.3)',
                padding: '10px 15px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                textAlign: 'left',
                transition: 'all 0.3s ease'
              }}
            >
              <FontAwesomeIcon icon={faBriefcase} /> Specialization
            </button>
            <button 
              onClick={() => document.querySelector('[data-section="services"]')?.scrollIntoView({ behavior: 'smooth' })}
              style={{
                background: 'rgba(0,0,0,0.3)',
                color: 'white',
                border: '2px solid rgba(255,255,255,0.3)',
                padding: '10px 15px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                textAlign: 'left',
                transition: 'all 0.3s ease'
              }}
            >
              <FontAwesomeIcon icon={faHandshake} /> Services
            </button>
            <button 
              onClick={() => document.querySelector('[data-section="social"]')?.scrollIntoView({ behavior: 'smooth' })}
              style={{
                background: 'rgba(0,0,0,0.3)',
                color: 'white',
                border: '2px solid rgba(255,255,255,0.3)',
                padding: '10px 15px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                textAlign: 'left',
                transition: 'all 0.3s ease'
              }}
            >
              <FontAwesomeIcon icon={faShareAlt} /> Social Media
            </button>
          </div>
        </div>
        
        {/* Profile Completion */}
        <div style={{ 
          background: 'rgba(255,255,255,0.15)',
          backdropFilter: 'blur(10px)',
          padding: '20px',
          borderRadius: '12px',
          border: '2px solid rgba(255,255,255,0.2)'
        }}>
          <h4 style={{ 
            color: '#ffffff', 
            fontSize: '1rem', 
            fontWeight: '700',
            marginBottom: '12px',
            textShadow: '0 2px 4px rgba(0,0,0,0.5)'
          }}>
            Profile Completion
          </h4>
          <div style={{
            background: 'rgba(0,0,0,0.2)',
            borderRadius: '10px',
            height: '10px',
            overflow: 'hidden',
            marginBottom: '10px'
          }}>
            <div style={{
              background: 'linear-gradient(90deg, #10b981 0%, #14b8a6 100%)',
              height: '100%',
              width: `${profileCompletionPercentage}%`,
              transition: 'width 0.5s ease',
              boxShadow: '0 0 10px rgba(16, 185, 129, 0.8)'
            }}></div>
          </div>
          <p style={{ 
            color: '#ffffff', 
            fontSize: '14px', 
            margin: 0,
            textShadow: '0 1px 2px rgba(0,0,0,0.5)',
            fontWeight: '600'
          }}>
            {profileCompletionPercentage}% Complete
          </p>
          {profileCompletionPercentage < 100 && !isViewingOtherProfile && (
            <button
              onClick={() => navigate('/employer-registration')}
              style={{
                width: '100%',
                marginTop: '15px',
                background: 'rgba(255,255,255,0.9)',
                color: '#f97316',
                border: 'none',
                padding: '10px',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              <FontAwesomeIcon icon={faEdit} /> Complete Your Profile
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content" style={mainContentStyles}>
      <div style={responsiveContainerStyle}>
        {/* Profile Header */}
        <div className="employer-profile-header profile-header" style={profileHeaderStyle}>
          <div style={headerPatternStyle}></div>
          
          {/* Role-aware back button - Only show when viewing another user's profile */}
          {isViewingOtherProfile && !isMobileView && (
            <button
              onClick={() => {
                if (viewerRole === 'employer') {
                  navigate('/employer-dashboard');
                } else if (viewerRole === 'recruiter') {
                  navigate('/recruiter-dashboard');
                } else {
                  navigate('/admin?view=profile-browser');
                }
              }}
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                zIndex: 10,
                background: 'rgba(255, 255, 255, 0.25)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(255, 255, 255, 0.4)',
                color: '#ffffff',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                transition: 'all 0.3s ease',
                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
              }}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              {viewerRole === 'employer' || viewerRole === 'recruiter'
                ? 'Back to Dashboard'
                : 'Back to Profile Browser'}
            </button>
          )}
          
          <div
            className="employer-profile-header-body profile-header-body"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: isMobileView ? '14px' : '30px',
              flexWrap: isMobileView ? 'wrap' : 'nowrap',
              position: 'relative',
              zIndex: 2,
              width: '100%'
            }}
          >
            <div
              className="employer-profile-header-identity profile-header-identity"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: isMobileView ? '12px' : '20px',
                flex: isMobileView ? '1 1 100%' : '1 1 auto',
                minWidth: 0
              }}
            >
              <div className="employer-profile-avatar-wrap" style={{ position: 'relative', flexShrink: 0 }}>
                <div style={profileAvatarStyle} className="employer-profile-avatar" aria-label="Profile photo">
                  {hasProfilePhoto && (
                    <img
                      src={profilePhotoSrc}
                      alt=""
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '50%'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const fallback = e.target.nextElementSibling;
                        if (fallback) {
                          fallback.style.display = 'flex';
                        }
                      }}
                    />
                  )}
                  <div
                    className="employer-profile-avatar-initials"
                    style={{
                      display: hasProfilePhoto ? 'none' : 'flex',
                      width: '100%',
                      height: '100%',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {profileInitials}
                  </div>
                </div>
                {!isViewingOtherProfile && (
                  <label
                    className="employer-profile-photo-btn"
                    style={{
                      position: 'absolute',
                      bottom: '0',
                      right: '0',
                      background: '#f97316',
                      color: 'white',
                      border: '3px solid white',
                      borderRadius: '50%',
                      width: isMobileView ? '28px' : '32px',
                      height: isMobileView ? '28px' : '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                      transition: 'all 0.3s ease',
                      zIndex: 10
                    }}
                    title="Change photo"
                  >
                    <FontAwesomeIcon icon={faCamera} style={{ fontSize: isMobileView ? '12px' : '14px' }} />
                    <input
                      type="file"
                      ref={profilePhotoInputRef}
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      style={{ display: 'none' }}
                    />
                  </label>
                )}
              </div>

              <div className="employer-profile-header-details profile-header-details" style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <h2
                    style={{
                      margin: 0,
                      fontSize: isMobileView ? '1.25rem' : '32px',
                      fontWeight: '700',
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      color: '#ffffff',
                      lineHeight: 1.25
                    }}
                  >
                    {profileForm.firstName} {profileForm.lastName}
                  </h2>
                  {hasAssociationVerification && (
                    <CommunityMemberBadge
                      type="association"
                      size="small"
                      label={associationBadgeLabel}
                    />
                  )}
                </div>
                <p
                  className="employer-profile-location-row profile-location-row"
                  style={{
                    margin: '6px 0 0',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: 'rgba(255,255,255,0.92)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    flexWrap: 'wrap'
                  }}
                >
                  <FontAwesomeIcon icon={faMapMarkerAlt} style={{ flexShrink: 0 }} />
                  <span className="employer-profile-location-text profile-location-text">
                    {getPrimaryLocationDisplay()}
                  </span>
                  {getMapsUrl() && (
                    <button
                      type="button"
                      className="employer-profile-maps-btn"
                      onClick={openLocationInMaps}
                      aria-label="Open location in Google Maps"
                      style={{
                        width: '32px',
                        height: '32px',
                        padding: 0,
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.35)',
                        background: 'rgba(255, 255, 255, 0.18)',
                        color: '#ffffff',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        flexShrink: 0
                      }}
                    >
                      <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: '13px' }} />
                    </button>
                  )}
                </p>
              </div>
            </div>

            {!isViewingOtherProfile && (
              <div className="employer-dashboard-back-desktop" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button 
                  onClick={() => navigate('/employer-dashboard')}
                  style={{
                    background: '#f97316',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <FontAwesomeIcon icon={faArrowLeft} /> Back to Dashboard
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Company Information Section */}
        <div style={{ ...elevatedCardStyle, padding: '25px' }} data-section="company">
          <div className="employer-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f0f0f0' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', margin: 0 }}>
              <FontAwesomeIcon icon={faBuilding} style={{ color: '#f97316', marginRight: '10px' }} /> 
              Company Information
            </h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <EditButton section="company" />
            </div>
          </div>
          
          {/* Debug info - remove after fixing */}
          {process.env.NODE_ENV === 'development' && (
            <div style={{ marginBottom: '10px', padding: '10px', background: '#f0f9ff', borderRadius: '6px', fontSize: '12px', color: '#0369a1' }}>
              <strong>Debug:</strong> companyName="{profileForm.companyName}", firstName="{profileForm.firstName}"
            </div>
          )}

          {/* Company Logo Upload (in edit mode only) */}
          {editingSections.company && (
            <>
              <div style={{ marginBottom: '15px', padding: '15px', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#64748b', fontWeight: '600' }}>
                  <FontAwesomeIcon icon={faImage} style={{ marginRight: '8px', color: '#f97316' }} />
                  Company Logo (displayed in profile header)
                </p>
                <label className="file-upload-label">
                  <FontAwesomeIcon icon={faImage} style={{ fontSize: '24px', color: '#f97316' }} />
                  <div>
                    <strong>Upload Company Logo</strong>
                    <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>Click to browse or drag and drop (PNG, JPG, max 5MB)</p>
                  </div>
                  <input type="file" accept="image/*" onChange={handleLogoChange} />
                </label>
              </div>
            </>
          )}

          <div style={{ ...gridStyle, gridTemplateColumns: '1fr', marginBottom: '15px' }}>
            <div className="form-group">
              <label className="form-label">Company Name <span className="required">*</span></label>
              <input 
                type="text"
                style={getViewModeStyle('company')}
                value={profileForm.companyName || ''} 
                disabled={!editingSections.company} 
                onChange={(e) => setProfileForm(p => ({ ...p, companyName: e.target.value }))} 
                placeholder="Enter company name"
              />
            </div>
          </div>

          <div style={grid2Style}>
            <div className="form-group">
              <label className="form-label">Company Email <span className="required">*</span></label>
              <input 
                type="email"
                style={getViewModeStyle('company')}
                value={profileForm.companyEmail || ''} 
                disabled={!editingSections.company} 
                onChange={(e) => setProfileForm(p => ({ ...p, companyEmail: e.target.value }))} 
                placeholder="contact@company.com"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Company Phone <span className="required">*</span></label>
              <input 
                type="tel"
                style={getViewModeStyle('company')}
                value={profileForm.companyPhone || ''} 
                disabled={!editingSections.company} 
                onChange={(e) => setProfileForm(p => ({ ...p, companyPhone: e.target.value }))} 
                placeholder="+1 (555) 123-4567"
              />
            </div>
          </div>

          <div style={{ ...gridStyle, gridTemplateColumns: '1fr', marginBottom: '15px' }}>
            <div className="form-group">
              <label className="form-label">Company Website</label>
              <input 
                type="url"
                style={getViewModeStyle('company')}
                value={profileForm.companyWebsite || ''} 
                disabled={!editingSections.company} 
                onChange={(e) => setProfileForm(p => ({ ...p, companyWebsite: e.target.value }))} 
                placeholder="https://www.yourcompany.com"
              />
            </div>
          </div>

          <div style={grid2Style}>
            <div className="form-group">
              <label className="form-label">Company Size <span className="required">*</span></label>
              <select 
                style={getViewModeStyle('company')}
                value={profileForm.companySize || ''} 
                disabled={!editingSections.company} 
                onChange={(e) => setProfileForm(p => ({ ...p, companySize: e.target.value }))}
              >
                <option value="">Select company size</option>
                <option value="1-10">1-10 employees</option>
                <option value="11-50">11-50 employees</option>
                <option value="51-200">51-200 employees</option>
                <option value="201-500">201-500 employees</option>
                <option value="501-1000">501-1000 employees</option>
                <option value="1001-5000">1001-5000 employees</option>
                <option value="5001+">5001+ employees</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Year Founded <span className="required">*</span></label>
              <input 
                type="number"
                style={getViewModeStyle('company')}
                value={profileForm.yearFounded || ''} 
                disabled={!editingSections.company} 
                onChange={(e) => setProfileForm(p => ({ ...p, yearFounded: e.target.value }))} 
                placeholder="2015"
                min="1800"
                max="2025"
              />
            </div>
          </div>

          <div style={{ ...gridStyle, gridTemplateColumns: '1fr', marginBottom: '15px' }}>
            <div className="form-group">
              <label className="form-label">Industry/Sector <span className="required">*</span></label>
              {editingSections.company ? (
                <>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <select 
                      style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                      value={industryInput}
                      onChange={(e) => setIndustryInput(e.target.value)}
                    >
                      <option value="">Select industries</option>
                      {STANDARD_INDUSTRIES.filter(industry => !profileForm.industries.includes(industry)).map((industry) => (
                        <option key={industry} value={industry}>
                          {industry}
                        </option>
                      ))}
                    </select>
                    <button 
                      type="button" 
                      className="add-btn" 
                      onClick={addIndustry}
                      disabled={!industryInput}
                      style={{ padding: '8px 16px' }}
                    >
                      <FontAwesomeIcon icon={faPlus} /> Add
                    </button>
                  </div>
                  {profileForm.industries.length > 0 && (
                    <div className="tags-container" style={{ marginTop: '8px' }}>
                      {profileForm.industries.map((industry, index) => (
                        <span key={index} className="tag">
                          {industry}
                          <span className="remove" onClick={() => removeIndustry(index)}>
                            <FontAwesomeIcon icon={faTimes} />
                          </span>
                        </span>
                      ))}
                    </div>
                  )}
                  {profileForm.industries.length === 0 && (
                    <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '4px' }}>Please add at least one industry</p>
                  )}
                </>
              ) : (
                <div style={{ padding: '8px', color: '#333' }}>
                  {profileForm.industries && profileForm.industries.length > 0 ? (
                    <div className="tags-container">
                      {profileForm.industries.map((industry, index) => (
                        <span key={index} className="tag" style={{ cursor: 'default' }}>
                          {industry}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span style={{ color: '#999' }}>No industries selected</span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div style={{ ...gridStyle, gridTemplateColumns: '1fr' }}>
            <div className="form-group">
              <label className="form-label">Company Description <span className="required">*</span></label>
              <textarea 
                style={{ ...getViewModeStyle('company'), minHeight: '120px', resize: 'vertical' }} 
                value={profileForm.companyDescription || ''} 
                disabled={!editingSections.company} 
                onChange={(e) => setProfileForm(p => ({ ...p, companyDescription: e.target.value }))} 
                placeholder="Describe your company, its mission, vision, and what makes it a great place to work..."
                rows="4"
              />
            </div>
          </div>

          <div style={{ marginTop: '20px', padding: '15px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <div className="form-group" style={{ marginBottom: '15px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: editingSections.company ? 'pointer' : 'default', marginBottom: '10px' }}>
                <input
                  type="checkbox"
                  checked={profileForm.showLogoOnWebsite === true}
                  disabled={!editingSections.company}
                  onChange={(e) => setProfileForm(p => ({ ...p, showLogoOnWebsite: e.target.checked }))}
                  style={{ width: '18px', height: '18px', cursor: editingSections.company ? 'pointer' : 'not-allowed' }}
                />
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#1e293b' }}>
                  Show your company logo in the employers page
                </span>
              </label>
              <p style={{ margin: '5px 0 0 28px', fontSize: '12px', color: '#64748b' }}>
                When enabled, your company logo will be displayed on the public Employers page.
              </p>
            </div>
            
            {editingSections.company && (
              <div className="form-group">
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b', marginBottom: '8px', display: 'block' }}>
                  Upload Company Logo
                </label>
                {logoPreview && (
                  <div style={{ marginBottom: '10px', textAlign: 'center' }}>
                    <img 
                      src={resolveLogoUrl(logoPreview)} 
                      alt="Company Logo Preview" 
                      style={{ maxWidth: '200px', maxHeight: '100px', objectFit: 'contain', borderRadius: '4px', border: '1px solid #e2e8f0' }}
                    />
                  </div>
                )}
                <label className="file-upload-label" style={{ cursor: 'pointer', padding: '12px', border: '1px dashed #cbd5e1', borderRadius: '6px', background: '#fff', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <FontAwesomeIcon icon={faImage} style={{ fontSize: '20px', color: '#f97316' }} />
                  <div style={{ flex: 1 }}>
                    <strong style={{ fontSize: '13px', display: 'block', marginBottom: '2px' }}>Choose Logo File</strong>
                    <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>PNG, JPG (max 5MB)</p>
                  </div>
                  <input 
                    type="file" 
                    ref={companyLogoInputRef}
                    accept="image/png,image/jpeg,image/jpg" 
                    onChange={handleLogoChange}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Company Location Section */}
        <div style={{ ...elevatedCardStyle, padding: '25px' }} data-section="location">
          <div className="employer-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f0f0f0' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', margin: 0 }}>
              <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: '#f97316', marginRight: '10px' }} /> 
              Company Location
            </h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <EditButton section="location" />
            </div>
          </div>

          <div style={grid2Style}>
            <div className="form-group">
              <label className="form-label">Country <span className="required">*</span></label>
              <select 
                style={getViewModeStyle('location')}
                value={profileForm.country || ''} 
                disabled={!editingSections.location} 
                onChange={(e) => setProfileForm(p => ({ ...p, country: e.target.value }))}
              >
                <option value="">Select your country</option>
                {COUNTRIES.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">State/Province <span className="required">*</span></label>
              <input 
                type="text"
                style={getViewModeStyle('location')}
                value={profileForm.state || ''} 
                disabled={!editingSections.location} 
                onChange={(e) => setProfileForm(p => ({ ...p, state: e.target.value }))} 
                placeholder="e.g., California"
              />
            </div>
          </div>

          <div style={grid2Style}>
            <div className="form-group">
              <label className="form-label">City <span className="required">*</span></label>
              <input 
                type="text"
                style={getViewModeStyle('location')}
                value={profileForm.city || ''} 
                disabled={!editingSections.location} 
                onChange={(e) => setProfileForm(p => ({ ...p, city: e.target.value }))} 
                placeholder="e.g., San Francisco"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Postal/ZIP Code</label>
              <input 
                type="text"
                style={getViewModeStyle('location')}
                value={profileForm.postalCode || ''} 
                disabled={!editingSections.location} 
                onChange={(e) => setProfileForm(p => ({ ...p, postalCode: e.target.value }))} 
                placeholder="94102"
              />
            </div>
          </div>

          <div style={{ ...gridStyle, gridTemplateColumns: '1fr', marginBottom: '15px' }}>
            <div className="form-group">
              <label className="form-label">Full Address <span className="required">*</span></label>
              <input 
                type="text"
                style={getViewModeStyle('location')}
                value={profileForm.address || ''} 
                disabled={!editingSections.location} 
                onChange={(e) => setProfileForm(p => ({ ...p, address: e.target.value }))} 
                placeholder="Street address, building name, suite number"
              />
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <GoogleMapEmbed
              address={mapQuery}
              latitude={profileForm.latitude}
              longitude={profileForm.longitude}
              height={260}
              label="Location Preview"
            />
          </div>

          {/* Map Section */}
          {editingSections.location && (
            <div className="form-group">
              <label className="form-label">Pin Your Office Location on Map (Optional)</label>
              <div className="info-badge">
                <FontAwesomeIcon icon={faMapMarkerAlt} /> Click on the map to mark your office location
              </div>
              
              <div ref={mapRef} className="map-container" style={{ height: '300px', width: '100%', marginTop: '10px' }}>
                {!mapLoaded && (
                  <div style={{ 
                    position: 'absolute', 
                    top: '50%', 
                    left: '50%', 
                    transform: 'translate(-50%, -50%)', 
                    color: '#666',
                    fontSize: '14px'
                  }}>
                    Loading map...
                  </div>
                )}
              </div>
              {mapLoaded && (
                <div className="coordinates-display">
                  <strong>Selected Coordinates:</strong> 
                  {profileForm.latitude && profileForm.longitude 
                    ? ` Lat: ${parseFloat(profileForm.latitude).toFixed(6)}, Lng: ${parseFloat(profileForm.longitude).toFixed(6)}`
                    : ' Click on the map to select'}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Employer Personal Details Section */}
        <div style={{ ...elevatedCardStyle, padding: '25px' }} data-section="personal">
          <div className="employer-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f0f0f0' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', margin: 0 }}>
              <FontAwesomeIcon icon={faUserTie} style={{ color: '#f97316', marginRight: '10px' }} /> 
              Employer Personal Details
            </h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <EditButton section="personal" />
            </div>
          </div>

          <div style={grid2Style}>
            <div className="form-group">
              <label className="form-label">First Name <span className="required">*</span></label>
              <input 
                type="text"
                style={getViewModeStyle('personal')}
                value={profileForm.firstName || ''} 
                disabled={!editingSections.personal} 
                onChange={(e) => setProfileForm(p => ({ ...p, firstName: e.target.value }))} 
                placeholder="Enter your first name"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name <span className="required">*</span></label>
              <input 
                type="text"
                style={getViewModeStyle('personal')}
                value={profileForm.lastName || ''} 
                disabled={!editingSections.personal} 
                onChange={(e) => setProfileForm(p => ({ ...p, lastName: e.target.value }))} 
                placeholder="Enter your last name"
              />
            </div>
          </div>

          <div style={grid2Style}>
            <div className="form-group">
              <label className="form-label">Job Title <span className="required">*</span></label>
              <input 
                type="text"
                style={getViewModeStyle('personal')}
                value={profileForm.jobTitle || ''} 
                disabled={!editingSections.personal} 
                onChange={(e) => setProfileForm(p => ({ ...p, jobTitle: e.target.value }))} 
                placeholder="e.g., CEO, Founder, HR Director, Business Owner"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Direct Phone Number <span className="required">*</span></label>
              <input 
                type="tel"
                style={getViewModeStyle('personal')}
                value={profileForm.recruiterPhone || ''} 
                disabled={!editingSections.personal} 
                onChange={(e) => setProfileForm(p => ({ ...p, recruiterPhone: e.target.value }))} 
                placeholder="+1 (555) 123-4567"
              />
            </div>
          </div>

          <div style={{ ...gridStyle, gridTemplateColumns: '1fr', marginBottom: '15px' }}>
            <div className="form-group">
              <label className="form-label">Professional Email <span className="required">*</span></label>
              <input 
                type="email"
                style={getViewModeStyle('personal')}
                value={profileForm.recruiterEmail || ''} 
                disabled={!editingSections.personal} 
                onChange={(e) => setProfileForm(p => ({ ...p, recruiterEmail: e.target.value }))} 
                placeholder="your.name@company.com"
              />
            </div>
          </div>

          <div style={{ ...gridStyle, gridTemplateColumns: '1fr' }}>
            <div className="form-group">
              <label className="form-label">LinkedIn Profile</label>
              <input 
                type="url"
                style={getViewModeStyle('personal')}
                value={profileForm.linkedinProfile || ''} 
                disabled={!editingSections.personal} 
                onChange={(e) => setProfileForm(p => ({ ...p, linkedinProfile: e.target.value }))} 
                placeholder="https://www.linkedin.com/in/yourprofile"
              />
            </div>
          </div>

          <div style={{ marginTop: '15px' }}>
            <div className="form-group" style={{ width: '100%' }}>
              <label className="form-label">Community / Association</label>
              {editingSections.personal ? (
                <CommunityAssociationSelector
                  value={profileForm.communitiesAssociations || []}
                  onChange={(items) => setProfileForm(p => ({ ...p, communitiesAssociations: items }))}
                />
              ) : (
                <div style={{ padding: '12px', background: '#f9f9f9', borderRadius: '6px', minHeight: '40px' }}>
                  {profileForm.communitiesAssociations && profileForm.communitiesAssociations.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {profileForm.communitiesAssociations.map((item, index) => (
                        <div key={index} style={{
                          padding: '8px 12px',
                          background: 'white',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <div>
                            <strong>{item.name}</strong>
                            <span style={{
                              marginLeft: '10px',
                              padding: '2px 8px',
                              background: item.type === 'association' ? '#e3f2fd' : '#f3e5f5',
                              color: item.type === 'association' ? '#1976d2' : '#7b1fa2',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '500'
                            }}>
                              {item.type === 'association' ? 'Association' : 'Community'}
                            </span>
                            {item.country && (
                              <span style={{ marginLeft: '8px', fontSize: '12px', color: '#666' }}>
                                • {item.country}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span style={{ color: '#999', fontStyle: 'italic' }}>No communities/associations added</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recruitment Specialization Section */}
        <div style={{ ...elevatedCardStyle, padding: '25px' }} data-section="specialization">
          <div className="employer-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f0f0f0' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', margin: 0 }}>
              <FontAwesomeIcon icon={faBriefcase} style={{ color: '#f97316', marginRight: '10px' }} /> 
              Recruitment Specialization
            </h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <EditButton section="specialization" />
            </div>
          </div>

          {/* Industries You Recruit For */}
          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label className="form-label">Industries You Recruit For <span className="required">*</span></label>
            {editingSections.specialization && (
              <>
                <div className="info-badge">
                  <FontAwesomeIcon icon={faInfoCircle} /> Add all industries you actively recruit for
                </div>
                <div className="skills-input-container">
                  <select 
                    value={industryInput}
                    onChange={(e) => setIndustryInput(e.target.value)}
                  >
                    <option value="">Select an industry</option>
                    {STANDARD_INDUSTRIES.map((industry) => (
                      <option key={industry} value={industry}>
                        {industry}
                      </option>
                    ))}
                  </select>
                  <button type="button" className="add-btn" onClick={addIndustry}>
                    <FontAwesomeIcon icon={faPlus} /> Add
                  </button>
                </div>
              </>
            )}
            <div style={{ marginTop: '10px' }}>
              {profileForm.industries && profileForm.industries.length > 0 ? (
                profileForm.industries.map((industry, index) => (
                  <span key={index} className="tag">
                    {industry}
                    {editingSections.specialization && (
                      <span className="remove" onClick={() => removeIndustry(index)}>
                        <FontAwesomeIcon icon={faTimes} />
                      </span>
                    )}
                  </span>
                ))
              ) : (
                <p style={{ color: '#64748b', fontStyle: 'italic' }}>No industries added yet</p>
              )}
            </div>
          </div>

          {/* Job Functions You Recruit For */}
          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label className="form-label">Job Functions You Recruit For <span className="required">*</span></label>
            {editingSections.specialization && (
              <>
                <div className="info-badge">
                  <FontAwesomeIcon icon={faInfoCircle} /> Type or select from dropdown
                </div>
                <div className="skills-input-container">
                  <input 
                    type="text" 
                    list="jobFunctionsListProfile"
                    placeholder="Type or select job function"
                    value={functionInput}
                    onChange={(e) => setFunctionInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFunction())}
                  />
                  <datalist id="jobFunctionsListProfile">
                    {globalSkillsData.skills.map((skill, index) => (
                      <option key={index} value={skill} />
                    ))}
                  </datalist>
                  <button type="button" className="add-btn" onClick={addFunction}>
                    <FontAwesomeIcon icon={faPlus} /> Add
                  </button>
                </div>
              </>
            )}
            <div style={{ marginTop: '10px' }}>
              {profileForm.functions && profileForm.functions.length > 0 ? (
                profileForm.functions.map((func, index) => (
                  <span key={index} className="tag">
                    {func}
                    {editingSections.specialization && (
                      <span className="remove" onClick={() => removeFunction(index)}>
                        <FontAwesomeIcon icon={faTimes} />
                      </span>
                    )}
                  </span>
                ))
              ) : (
                <p style={{ color: '#64748b', fontStyle: 'italic' }}>No job functions added yet</p>
              )}
            </div>
          </div>

          {/* Average Hiring Volume */}
          <div style={{ ...gridStyle, gridTemplateColumns: '1fr' }}>
            <div className="form-group">
              <label className="form-label">Average Hiring Volume (Monthly)</label>
              <select 
                style={getViewModeStyle('specialization')}
                value={profileForm.hiringVolume || ''} 
                disabled={!editingSections.specialization} 
                onChange={(e) => setProfileForm(p => ({ ...p, hiringVolume: e.target.value }))}
              >
                <option value="">Select hiring volume</option>
                <option value="1-5">1-5 positions</option>
                <option value="6-10">6-10 positions</option>
                <option value="11-20">11-20 positions</option>
                <option value="21-50">21-50 positions</option>
                <option value="51+">51+ positions</option>
              </select>
            </div>
          </div>
        </div>

        {/* Services & Offerings Section */}
        <div style={{ ...elevatedCardStyle, padding: '25px' }} data-section="services">
          <div className="employer-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f0f0f0' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', margin: 0 }}>
              <FontAwesomeIcon icon={faHandshake} style={{ color: '#f97316', marginRight: '10px' }} /> 
              Services & Offerings
            </h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <EditButton section="services" />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Types of Employment You Offer <span className="required">*</span></label>
            {editingSections.services ? (
              <>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <select 
                    style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                    value={employmentTypeInput}
                    onChange={(e) => setEmploymentTypeInput(e.target.value)}
                  >
                    <option value="">Select employment types</option>
                    {!profileForm.employmentTypes.includes('full-time') && (
                      <option value="full-time">Full-time Positions</option>
                    )}
                    {!profileForm.employmentTypes.includes('part-time') && (
                      <option value="part-time">Part-time Positions</option>
                    )}
                    {!profileForm.employmentTypes.includes('contract') && (
                      <option value="contract">Contract/Temporary</option>
                    )}
                    {!profileForm.employmentTypes.includes('freelance') && (
                      <option value="freelance">Freelance/Project-based</option>
                    )}
                    {!profileForm.employmentTypes.includes('internship') && (
                      <option value="internship">Internships</option>
                    )}
                  </select>
                  <button 
                    type="button" 
                    className="add-btn" 
                    onClick={addEmploymentType}
                    disabled={!employmentTypeInput}
                    style={{ padding: '8px 16px' }}
                  >
                    <FontAwesomeIcon icon={faPlus} /> Add
                  </button>
                </div>
                {profileForm.employmentTypes.length > 0 && (
                  <div className="tags-container" style={{ marginTop: '8px' }}>
                    {profileForm.employmentTypes.map((type, index) => (
                      <span key={index} className="tag">
                        {type === 'full-time' ? 'Full-time Positions' :
                         type === 'part-time' ? 'Part-time Positions' :
                         type === 'contract' ? 'Contract/Temporary' :
                         type === 'freelance' ? 'Freelance/Project-based' :
                         type === 'internship' ? 'Internships' : type}
                        <span className="remove" onClick={() => removeEmploymentType(index)}>
                          <FontAwesomeIcon icon={faTimes} />
                        </span>
                      </span>
                    ))}
                  </div>
                )}
                {profileForm.employmentTypes.length === 0 && (
                  <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '4px' }}>Please add at least one employment type</p>
                )}
              </>
            ) : (
              <div style={{ padding: '8px', color: '#333' }}>
                {profileForm.employmentTypes && profileForm.employmentTypes.length > 0 ? (
                  <div className="tags-container">
                    {profileForm.employmentTypes.map((type, index) => (
                      <span key={index} className="tag" style={{ cursor: 'default' }}>
                        {type === 'full-time' ? 'Full-time Positions' :
                         type === 'part-time' ? 'Part-time Positions' :
                         type === 'contract' ? 'Contract/Temporary' :
                         type === 'freelance' ? 'Freelance/Project-based' :
                         type === 'internship' ? 'Internships' : type}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span style={{ color: '#999' }}>No employment types specified</span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Social Media Section */}
        <div style={{ ...elevatedCardStyle, padding: '25px' }} data-section="social">
          <div className="employer-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f0f0f0' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', margin: 0 }}>
              <FontAwesomeIcon icon={faShareAlt} style={{ color: '#f97316', marginRight: '10px' }} /> 
              Social Media & Online Presence
            </h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <EditButton section="social" />
            </div>
          </div>

          <div style={grid2Style}>
            <div className="form-group">
              <label className="form-label">Company LinkedIn Page</label>
              <input 
                type="url"
                style={getViewModeStyle('social')}
                value={profileForm.linkedinCompany || ''} 
                disabled={!editingSections.social} 
                onChange={(e) => setProfileForm(p => ({ ...p, linkedinCompany: e.target.value }))} 
                placeholder="https://www.linkedin.com/company/yourcompany"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Facebook Page</label>
              <input 
                type="url"
                style={getViewModeStyle('social')}
                value={profileForm.facebook || ''} 
                disabled={!editingSections.social} 
                onChange={(e) => setProfileForm(p => ({ ...p, facebook: e.target.value }))} 
                placeholder="https://www.facebook.com/yourcompany"
              />
            </div>
          </div>

          <div style={grid2Style}>
            <div className="form-group">
              <label className="form-label">Twitter/X Handle</label>
              <input 
                type="url"
                style={getViewModeStyle('social')}
                value={profileForm.twitter || ''} 
                disabled={!editingSections.social} 
                onChange={(e) => setProfileForm(p => ({ ...p, twitter: e.target.value }))} 
                placeholder="https://twitter.com/yourcompany"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Instagram</label>
              <input 
                type="url"
                style={getViewModeStyle('social')}
                value={profileForm.instagram || ''} 
                disabled={!editingSections.social} 
                onChange={(e) => setProfileForm(p => ({ ...p, instagram: e.target.value }))} 
                placeholder="https://www.instagram.com/yourcompany"
              />
            </div>
          </div>

          {/* Additional Professional Links */}
          <div className="form-group">
            <label className="form-label">Additional Professional Links</label>
            {editingSections.social && (
              <>
                <div className="employer-add-link-row" style={{ display: 'grid', gridTemplateColumns: isMobileView ? '1fr' : '1fr 2fr auto', gap: '10px', marginTop: '10px', marginBottom: '15px' }}>
                  <input 
                    type="text" 
                    placeholder="Link type (e.g., Portfolio, Blog)" 
                    value={linkTypeInput} 
                    onChange={(e) => setLinkTypeInput(e.target.value)}
                    style={formInputBase}
                  />
                  <input 
                    type="url" 
                    placeholder="https://example.com" 
                    value={linkUrlInput} 
                    onChange={(e) => setLinkUrlInput(e.target.value)}
                    style={formInputBase}
                  />
                  <button type="button" className="add-btn" onClick={addLink}>
                    <FontAwesomeIcon icon={faPlus} /> Add
                  </button>
                </div>
              </>
            )}
            <div style={{ marginTop: '10px' }}>
              {profileForm.additionalLinks && profileForm.additionalLinks.length > 0 ? (
                profileForm.additionalLinks.map((link, index) => (
                  <div key={index} className="link-item">
                    <div className="link-item-content">
                      <div className="link-item-type">
                        <FontAwesomeIcon icon={faLink} /> {link.type}
                      </div>
                      <div className="link-item-url">
                        <a href={link.url} target="_blank" rel="noopener noreferrer">{link.url}</a>
                      </div>
                    </div>
                    {editingSections.social && (
                      <button type="button" className="remove-item-btn" onClick={() => removeLink(index)}>
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    )}
                  </div>
                ))
              ) : (
                <p style={{ color: '#64748b', fontStyle: 'italic' }}>No additional links added</p>
              )}
            </div>
          </div>
        </div>

        {/* Additional Information Section */}
        <div style={{ ...elevatedCardStyle, padding: '25px' }} data-section="additional">
          <div className="employer-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f0f0f0' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', margin: 0 }}>
              <FontAwesomeIcon icon={faInfoCircle} style={{ color: '#f97316', marginRight: '10px' }} /> 
              Additional Information
            </h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <EditButton section="additional" />
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label className="form-label">Would you like to participate in any volunteering?</label>
            <select 
              style={getViewModeStyle('additional')}
              value={profileForm.participateInVolunteering || ''} 
              disabled={!editingSections.additional} 
              onChange={(e) => setProfileForm(p => ({ ...p, participateInVolunteering: e.target.value }))}
            >
              <option value="">Select an option</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          {profileForm.participateInVolunteering === 'yes' && (
            <div className="form-group">
              <label className="form-label">Volunteering Details</label>
              <textarea 
                style={{ ...getViewModeStyle('additional'), minHeight: '100px' }}
                value={profileForm.volunteeringDetails || ''} 
                disabled={!editingSections.additional} 
                onChange={(e) => setProfileForm(p => ({ ...p, volunteeringDetails: e.target.value }))} 
                placeholder="Please describe the volunteering activities you'd like to participate in"
              />
            </div>
          )}
        </div>

      </div>
      </div>
      </div>
    </>
  );
};

export default EmployerMyProfile;
